/*************************************************************
 * Imports
 *************************************************************/
// 1. Import both question files
import questionsEn from "../data/questions.json";
import questionsEs from "../data/questions-es.json";

// jQuery from window
const $ = window.jQuery;

/*************************************************************
 * Detect Language & Define Globals
 *************************************************************/
// Check if <html lang="es-ES">
const isSpanish = document.documentElement.lang === "es-ES";
// Use Spanish or English questions
const questions = isSpanish ? questionsEs : questionsEn;
// Where to redirect after submission
const resultsPath = isSpanish ? "/es/cuestionario/resultados" : "/quiz/results";
// Next button label
const nextLabel = isSpanish ? "Siguiente" : "Next";

/*************************************************************
 * ProgressBar
 *************************************************************/
export class ProgressBar {
  constructor({ progressBarEl, levelCount }) {
    this.$progressBarEl = progressBarEl instanceof jQuery ? progressBarEl : $(progressBarEl);
    this._levelCount = levelCount;
    this.$bar = this.$progressBarEl.find('.progress-bar__bar');
  }

  init() {
    this.setLevel(0);
  }

  setLevel(level) {
    if (level === this._levelCount) {
      this.$bar.addClass('progress-bar__bar--complete');
    }
    const percentage = (level / this._levelCount) * 100;
    this.$bar.css('width', `${percentage}%`);
  }

  complete() {
    this.setLevel(this._levelCount);
  }
}

/*************************************************************
 * ViewSwitch
 *************************************************************/
export class ViewSwitch {
  constructor({ viewSwitchEl, views = {} }) {
    this.$switchEl = viewSwitchEl instanceof jQuery ? viewSwitchEl : $(viewSwitchEl);
    this.views = Object.keys(views).reduce((acc, key) => {
      acc[key] = views[key] instanceof jQuery ? views[key] : $(views[key]);
      return acc;
    }, {});
    this._setZindex();
    const firstViewKey = Object.keys(this.views)[0];
    this.$mounted = this.views[firstViewKey];
  }

  _setZindex() {
    const viewValues = Object.values(this.views);
    viewValues.forEach((el, i) => {
      el.css('zIndex', i + 1);
    });
  }

  mount(view) {
    this.$mounted.addClass('view--exit');
    this.views[view].addClass('view--enter');
    this.$mounted = this.views[view];
  }
}

/*************************************************************
 * Template References
 *************************************************************/
export const RADIO_QUESTION_TEMPLATE = document.querySelector('#radio-question').content.firstElementChild;
export const RADIO_OPTION_TEMPLATE = document.querySelector('#radio-question-option').content.firstElementChild;
export const CHECKBOX_OPTION_TEMPLATE = document.querySelector('#checkbox-question-option').content.firstElementChild;
export const RADIO_BUTTON_QUESTION_TEMPLATE = document.querySelector('#radio-button-question-option').content.firstElementChild;
export const CHECKBOX_BUTTON_QUESTION_TEMPLATE = document.querySelector('#checkbox-button-question-option').content.firstElementChild;

/*************************************************************
 * Base Question
 *************************************************************/
export class Question {
  constructor({ id, type, question, options, color, helperText, subtype }) {
    this._id = id;
    this._type = type;       
    this._subtype = subtype; 
    this._question = question;
    this._helperText = helperText;
    this._options = options || [];
    this._color = color;
    this._inputsName = `question-${this._id}`;

    this.$element = null;
    this._arrowKeyPressed = false;
  }

  getHeight() {
    return this.$element ? this.$element.outerHeight() : 0;
  }

  hide() {
    if (this.$element) {
      this.$element.removeClass('question--appear');
    }
  }

  focusFirstInput() {
    if (this.$element) {
      this.$element.find('input').first().focus();
    }
  }

  appear() {
    if (this.$element) {
      this.$element.addClass('question--appear');
      setTimeout(() => {
        this.$element.find('input').removeAttr('disabled');
      }, 300);
    }
  }

  getElement() {
    return this.$element;
  }

  pass() {
    if (this.$element) {
      this.$element.addClass('question--pass');
    }
  }

  unpass() {
    if (this.$element) {
      this.$element.removeClass('question--pass');
    }
  }
}

/*************************************************************
 * Constants
 *************************************************************/
export const DOB_REGEX = "^\\d{2}\\/\\d{2}\\/\\d{4}$";
// In your JS file
export const IMAGES_BASE_URI =
  // Check if a global variable is defined
  window?.LASIK_QUIZ_IMAGES_BASE_URI
    // If yes, use that
    ? window.LASIK_QUIZ_IMAGES_BASE_URI
    // Otherwise, fallback to old path
    : "/wp-content/themes/lasik/gutenberg/blocks/lasik-quiz/images";


/*************************************************************
 * ChoiceQuestion (Radio or Checkbox, Bubble or Buttons)
 *************************************************************/
export class ChoiceQuestion extends Question {
  onSelect(fn) {
    this._onSelect = fn;
  }

  /**
   * Return the correct <li> template for either radio/checkbox + bubble/buttons
   */
  _getOptionTemplate() {
    const isRadio = (this._type === 'radio');
    const isButtonLayout = (this._subtype === 'buttons');

    if (isRadio && isButtonLayout) {
      return RADIO_BUTTON_QUESTION_TEMPLATE;
    } else if (isRadio && !isButtonLayout) {
      return RADIO_OPTION_TEMPLATE;
    } else if (!isRadio && isButtonLayout) {
      return CHECKBOX_BUTTON_QUESTION_TEMPLATE;
    } else {
      // checkbox + bubble
      return CHECKBOX_OPTION_TEMPLATE;
    }
  }

  /**
   * Build the main question DOM from the #radio-question container,
   * then fill in the Next button (if checkbox) and each option.
   */
  getView() {
    const $questionEl = $(RADIO_QUESTION_TEMPLATE).clone(true, true);

    // Question text + helper
    $questionEl.find('.question__heading').html(this._question);
    const $subheadingEl = $questionEl.find('.question__helper-text');
    if (this._helperText) {
      $subheadingEl.html(this._helperText);
    } else {
      $subheadingEl.remove();
    }

    $questionEl.addClass(`question--id--${this._id}`);
    const $optionListEl = $questionEl.find('.question__option-list');

    // Decide layout
    if (this._subtype === 'buttons') {
      $optionListEl.addClass('question__option-list--buttons');
    } else {
      if (this._options.length % 3) {
        if (this._options.length === 2) {
          $optionListEl.addClass('question__option-list--flex');
        } else {
          $optionListEl.addClass('question__option-list--grid--4');
        }
      }
    }

    // If it's a checkbox question, build the Next button using nextLabel
    let $nextBtn = null;
    if (this._type === 'checkbox') {
      const $nextBtnWrapper = $(`
        <div class="question__next-button-wrapper">
          <button 
            class="question__next-button button is-primary"
            type="button"
            disabled
          >
            ${nextLabel}<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M422.6 278.6L445.3 256l-22.6-22.6-144-144L256 66.7 210.8 112l22.6 22.6L322.8 224 32 224 0 224l0 64 32 0 290.7 0-89.4 89.4L210.8 400 256 445.3l22.6-22.6 144-144z"/></svg>
          </button>
        </div>
      `);

      $nextBtn = $nextBtnWrapper.find('.question__next-button');
      $questionEl.append($nextBtnWrapper);

      // When user clicks Next
      $nextBtn.on('click', () => {
        if (this._onSelect) {
          // We signal that it's a "nextButton" event
          this._onSelect({ nextButton: true }, this._type, this._subtype);
        }
      });
    }

    // For each option, clone from the correct <li> template
    const optionTemplate = this._getOptionTemplate();
    this._options.forEach(({ text, image, color }, i) => {
      const $optionEl = $(optionTemplate).clone(true, true);

      color = color || this._mapIndexToHex(i);
      $optionEl.find('.question__option-bubble-outter').css('backgroundColor', color);
      $optionEl.find('.question__option-bubble').css('backgroundColor', color);

      $optionEl.find('.question__option-label, .question__button-option-label').html(text);

      // Input config
      const $inputEl = $optionEl.find('input.question__input');
      $inputEl.attr('type', this._type);
      $inputEl.attr('name', this._inputsName);

      // If there's an image
      const $imageEl = $optionEl.find('.question__image');
      if (image && typeof image === 'string') {
        $imageEl.attr('src', `${IMAGES_BASE_URI}/${image}`);
      } else if (image && typeof image === 'object') {
        $imageEl.attr('src', `${IMAGES_BASE_URI}/${image.src}`);
        if (image.style) {
          $imageEl.addClass(`question__image--style--${image.style}`);
        }
      } else {
        $imageEl.remove();
      }

      this._attachEvents($inputEl, $questionEl, $nextBtn);
      $optionListEl.append($optionEl);
    });

    // Accessibility: trap Tab
    $(window).on('keydown', (event) => {
      if (event.key === 'Tab') {
        const $backBtn = $('.back-button');
        if ($(document.activeElement).is($backBtn)) {
          $questionEl.find('input').first().focus();
        } else {
          $backBtn.focus();
        }
        event.preventDefault();
      }
    });

    this.$element = $questionEl;
    return this.$element;
  }

  _attachEvents($inputEl, $questionEl, $nextBtn) {
    if (!this._onSelect) return;

    $inputEl.on('keydown', (event) => {
      event.stopPropagation();
      if (event.key.startsWith('Arrow')) {
        $questionEl.addClass('question--using-keyboard');
        this._arrowKeyPressed = true;
        return;
      }
      if (event.key === 'Enter') {
        this._onSelect(event, this._type, this._subtype);
      }
    });

    $inputEl.on('click', (e) => {
      if (this._arrowKeyPressed) {
        this._arrowKeyPressed = false;
        return;
      }

      if (this._type === 'radio') {
        // Radio: auto-advance
        if (e.target.checked) {
          this._onSelect(e, this._type, this._subtype);
        }
      } else {
        // Checkbox: do NOT auto-advance
        // Enable Next button once something is selected
        if ($nextBtn) {
          const $allChecks = $questionEl.find('input[type=checkbox]');
          const anyChecked = $allChecks.toArray().some(chk => chk.checked);
          $nextBtn.prop('disabled', !anyChecked);
        }
      }
    });
  }

  _mapIndexToHex(index) {
    return ['#00C881', '#06A1B5', '#ffc20e', '#00B374'][index] || '#00C881';
  }
}

/*************************************************************
 * QuizController
 *************************************************************/
export class QuizController {
  constructor({ container, questionObjs }) {
    this.$questionTrackEl = container instanceof jQuery ? container : $(container);
    this._questions = questionObjs;
    this._currentQuestionIndex = -1;
    this._currentQuestion = null;
  }

  currentQuestion() {
    return this._currentQuestion;
  }

  init() {
    this._questions.forEach((q) => {
      this.$questionTrackEl.append(q.getView());
    });
    this.forward();

    setTimeout(() => {
      this.$questionTrackEl.find('input').first().focus();
    }, 300);
  }

  isFirstQuestion() {
    return this._currentQuestionIndex === 0;
  }

  isLastQuestion() {
    return this._currentQuestionIndex === this._questions.length - 1;
  }

  getCurrentQuestionIndex() {
    return this._currentQuestionIndex;
  }

  getNextIndex() {
    return this._questions[this._currentQuestionIndex + 1]
      ? this._currentQuestionIndex + 1
      : false;
  }

  getPreviousIndex() {
    return this._questions[this._currentQuestionIndex - 1]
      ? this._currentQuestionIndex - 1
      : false;
  }

  _syncIndexToQuestion() {
    this._currentQuestion = this._questions[this._currentQuestionIndex];
  }

  _adjustHeight() {
    setTimeout(() => {
      if (this._currentQuestion) {
        this.$questionTrackEl.css('height', this._currentQuestion.getHeight() + 'px');
      }
    });
  }

  forward() {
    const nextQuestionIndex = this.getNextIndex();
    if (nextQuestionIndex === false) return false;
    if (this._currentQuestion) {
      this._currentQuestion.pass();
    }
    this._currentQuestionIndex = nextQuestionIndex;
    this._syncIndexToQuestion();
    this._currentQuestion.appear();
    this._adjustHeight();

    setTimeout(() => {
      this._currentQuestion.focusFirstInput();
    }, 300);
  }

  back() {
    const prevQuestionIndex = this.getPreviousIndex();
    if (prevQuestionIndex === false) return false;
    this._currentQuestion.hide();
    this._currentQuestionIndex = prevQuestionIndex;
    this._syncIndexToQuestion();
    this._currentQuestion.unpass();
    this._adjustHeight();

    setTimeout(() => {
      this._currentQuestion.focusFirstInput();
    }, 300);
  }
}

/*************************************************************
 * EventEmitter
 *************************************************************/
export class EventEmitter {
  constructor() {
    this.events = {};
  }

  on(eventName, listener) {
    if (!this.events[eventName]) {
      this.events[eventName] = [];
    }
    this.events[eventName].push(listener);
  }

  emit(eventName, ...args) {
    const listeners = this.events[eventName];
    if (listeners) {
      for (const listener of listeners) {
        listener.apply(this, args);
      }
    }
  }

  off(eventName, listener) {
    const listeners = this.events[eventName];
    if (listeners) {
      this.events[eventName] = listeners.filter((l) => l !== listener);
    }
  }
}

/*************************************************************
 * Main Quiz Script
 *************************************************************/
$(document).ready(function () {
  // Create the event emitter
  const ee = new EventEmitter();

  // DOM Elements
  const $prevBtnEl = $('#prev-btn');
  const $startBtnEl = $('#start-btn');
  const $submitButton = $('#submit-btn');
  const $progressBarEl = $('.progress-bar');
  const $viewSwitchEl = $('.view-switch');
  const $quizView = $('#quiz-view');
  const $welcomeView = $('#welcome-view');
  const $leadView = $('#lead-view');
  const $formEl = $('#form');
  const $questionTrackEl = $('#question-track');
  const $loadScreen = $('#load-screen');

  // Build the ProgressBar
  const progressBar = new ProgressBar({
    progressBarEl: $progressBarEl,
    levelCount: questions.length + 2
  });
  progressBar.init();

  // Build the ViewSwitch
  const viewController = new ViewSwitch({
    viewSwitchEl: $viewSwitchEl,
    views: {
      welcome: $welcomeView,
      quiz: $quizView,
      lead: $leadView
    }
  });

  // Build the question objects
  const questionObjs = questions.map((qData) => {
    const qObj = new ChoiceQuestion(qData);

    qObj.onSelect((event, qType, qSubtype) => {
      // If user clicked the Next button on a checkbox question
      if (event?.nextButton) {
        return ee.emit('next');
      }

      // If it's a radio question
      if (qType === 'radio') {
        if (qSubtype === 'buttons') {
          ee.emit('next');
        } else {
          setTimeout(() => ee.emit('next'), 300);
        }
      } else {
        // It's a checkbox, do nothing on each click
        console.log('Checkbox. User must click Next to proceed.');
      }
    });

    return qObj;
  });

  // Build the QuizController
  const quizController = new QuizController({
    container: $questionTrackEl,
    questionObjs
  });

  // Helpers
  const hideButton = ($button) => $button.addClass('lasik-quiz-button--hide');
  const showButton = ($button) => $button.removeClass('lasik-quiz-button--hide');
  const showLoadingScreen = () => $loadScreen.addClass('quiz-lead-view__spinner--show');
  const hideLoadingScreen = () => $loadScreen.removeClass('quiz-lead-view__spinner--show');

  // Prev Button
  $prevBtnEl.on('click', () => ee.emit('previous'));
  $prevBtnEl.on('keydown', (e) => {
    if (e.key === 'Tab') {
      quizController.currentQuestion()?.getElement().find('input').first().focus();
    }
  });

  // jQuery Mask if needed
  $('input[name="dob"]').mask("00/00/0000");
  $('input[name="phone"]').mask("(999) 999-9999");

  // Start quiz
  $startBtnEl.on('click', () => ee.emit('start'));

  // Form submission
  $formEl.on('submit', (e) => {
    e.preventDefault();
    showLoadingScreen();

    // Basic validation
    const $emailInput = $formEl.find('input[name="email"]');
    const $firstNameInput = $formEl.find('input[name="first_name"]');
    const $lastNameInput = $formEl.find('input[name="last_name"]');

    let errors = false;

    const emailVal = $emailInput.val();
    if (!emailVal || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      $emailInput.addClass('quiz-lead-view__form-input--error');
      errors = true;
    } else {
      $emailInput.removeClass('quiz-lead-view__form-input--error');
    }

    if (!$firstNameInput.val()) {
      $firstNameInput.addClass('quiz-lead-view__form-input--error');
      errors = true;
    } else {
      $firstNameInput.removeClass('quiz-lead-view__form-input--error');
    }

    if (!$lastNameInput.val()) {
      $lastNameInput.addClass('quiz-lead-view__form-input--error');
      errors = true;
    } else {
      $lastNameInput.removeClass('quiz-lead-view__form-input--error');
    }

    if (errors) {
      hideLoadingScreen();
      return;
    }

    // Gather data
    const firstName = $firstNameInput.val();
    const lastName = $lastNameInput.val();
    const phone = $formEl.find('input[name="phone"]').val();
    const email = $emailInput.val();
    const optIn = $formEl.find('input[name="optIn"]').is(':checked') ? 'Y' : 'N';

    // If Spanish, /es/cuestionario/resultados, else /quiz/results
    const finalResultsPath = isSpanish ? "/es/cuestionario/resultados" : "/quiz/results";

    fetch("/wp-json/lasik/v1/quiz-lead", {
      method: "POST",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ email, firstName, lastName, phone, optIn })
    })
      .then(r => r.json())
      .then(resp => console.log("Response:", resp))
      .finally(() => {
        window.location.href = finalResultsPath + "?fname=" + firstName;
      });
  });

  // Pub/Sub
  ee.on('previous', () => {
    quizController.back();
    progressBar.setLevel(quizController.getCurrentQuestionIndex() + 1);
    if (quizController.getPreviousIndex() === false) {
      hideButton($prevBtnEl);
    }
  });

  ee.on('next', () => {
    progressBar.setLevel(quizController.getCurrentQuestionIndex() + 1);
    if (quizController.getNextIndex() === false) {
      hideButton($prevBtnEl);
      ee.emit('show-lead');
      $submitButton.prop('disabled', false);
      return;
    }
    quizController.forward();
    if (quizController.getPreviousIndex() !== false) {
      showButton($prevBtnEl);
    }
  });

  ee.on('loading', showLoadingScreen);

  ee.on('start', () => {
    viewController.mount('quiz');
    quizController.init();
  });

  ee.on('show-lead', () => {
    const $formContainer = $('.quiz-lead-view__form');
    setTimeout(() => {
      $formContainer.find('input').first().focus();
    }, 300);

    $(window).on('keydown', function (event) {
      if (event.key === 'Tab') {
        const $focusable = $formContainer.find('input,button,select,textarea');
        if ($focusable.length) {
          const currentIndex = $focusable.index($(document.activeElement));
          const $next = $focusable.eq((currentIndex + 1) % $focusable.length);
          $next.focus();
          event.preventDefault();
        }
      }
    });
    viewController.mount('lead');
  });
});
